import { useMemo } from 'react';
import { Stack, Typography, Box, Skeleton } from '@mui/material';

import { useUserUnits } from 'selectors/user';
import { GeoLocation } from 'types/locationView';

import { calculateDistance } from 'utils/distanceUtils';
import classNames from 'classnames';
import {
  useLiveWindModalStationIsOffline,
  useLiveWindModalStationIsDataLagging,
} from 'hooks/liveWind/useLiveWindModal';

import type { Props } from '../LiveWindModal';

import styles from './ModalSubHeader.module.scss';

interface SubHeaderProps extends Pick<Props, 'geo'> {
  location?: string;
  loading?: boolean;
  windStationLocation?: GeoLocation;
}

const ModalSubHeader: React.FC<SubHeaderProps> = ({
  loading,
  location,
  geo,
  windStationLocation,
}) => {
  const units = useUserUnits();

  const { distance, distanceUnits } = useMemo(
    () => calculateDistance(units?.tideHeight, geo, windStationLocation),
    [windStationLocation, geo, units?.tideHeight],
  );

  const [stationIsOffline] = useLiveWindModalStationIsOffline();
  const [isDataLagging] = useLiveWindModalStationIsDataLagging();

  return (
    <Stack direction="row" alignItems="center">
      <Box
        className={classNames({
          [styles.statusCircle]: true,
          [styles.isOffline]: stationIsOffline || isDataLagging,
        })}
      />
      <Typography
        id="live-wind-modal-description"
        variant="callout2"
        component="p"
        color="text.secondary"
      >
        {loading ? (
          <Skeleton variant="rectangular" width={250} animation="wave" />
        ) : (
          `${location} (${distance}${distanceUnits} away)`
        )}
      </Typography>
    </Stack>
  );
};

export default ModalSubHeader;
